import React, { useEffect, useState, useContext } from "react";
import qahoy from "views_shared/app/javascript/scripts/qahoy";
import qapi from "views_shared/app/javascript/api";
import { expertUrl, companyUrl, prProfileUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import ListRow from "../../profiles/ListRow";
import Tag from "../../profiles/Tag";
import CompanyPersonRowSkeleton from "./company_person_row_skeleton";
import useInfiniteScroll from "../../hooks/use_infinite_scroll";
import { CompanyContext } from "../data/company_context_management";
import CompanyHighlightedExpert from "./company_highlighted_expert";

const MAX_HASHTAGS = 3;
const FIRST_PAGE_PLACEHOLDERS = 6;
const NEXT_PAGE_PLACEHOLDERS = 3;

// Event name predates the card-to-row conversion; kept verbatim to preserve the qahoy analytics series.
const HASHTAG_CLICK_EVENT = "Experts/ExpertCardMinimal#clickHashtag";

// The roster's chips reach the expert index's hashtag filter, which is where this population is
// searchable; the batched tag lookup has no way to know that on its own.
function expertHashtagUrl(hashtagName) {
  return `/experts?hashtag=${encodeURIComponent(hashtagName)}`;
}

// Only a promoter has a profile of their own to link to; every other kind of person on a company's
// roster — including every row on a PR agency's page, which is always a promo-entity expert —
// lives at /experts.
function expertPath(expert) {
  // The Promoter branch (/profiles/:slug) is the generic cross-type profile route (see `profileUrl`
  // in views_shared/utils/urls) — out of scope here since it carries no `/experts/` literal to
  // migrate; only the plain-expert branch below routes through the V1-aware helper.
  if (expert.profile_type === "Id::Profile::Promoter") {
    // V1-COMPAT: on V2 (V2_URL unset) this stays the exact `/profiles/:slug` path — prProfileUrl's
    // own V2-native fallback is `/pr_profiles/:slug`, a DIFFERENT page, so it can't be used
    // unconditionally here. Only when V1-hosted does the link need to become V1-aware.
    return globalThis.Qwoted?.V2_URL
      ? prProfileUrl({ slug: expert.slug, migrate_id: expert.migrate_id })
      : `/profiles/${expert.slug}`;
  }
  return expertUrl({ slug: expert.slug, migrate_id: expert.migrate_id });
}

// The dividers belong to the list, not the row: every row but the last carries the rule beneath it.
function dividerClasses(index, count) {
  return `py-2${index < count - 1 ? " border-bottom" : ""}`;
}

// Both feeds fan out one row per JOB — the representations view per employment of the represented
// expert, the company-jobs view per job a person holds here — but the roster lists each person
// once. First row per person wins (the views front-load featured/highlighted rows), and the seen
// set spans pages so infinite scroll can't reintroduce someone.
function appendUniquePeople(prevRecords, additions) {
  const seen = new Set(prevRecords.map((person) => `${person.profile_type}:${person.id}`));
  const fresh = additions.filter((person) => {
    const key = `${person.profile_type}:${person.id}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [...prevRecords, ...fresh];
}

// Duplicate of the expert-profile provider's own tag-ranking shape — this is that logic's second
// occurrence, not yet the signal to extract a shared helper. The view fans a tag out per
// representing profile, so a person's rows arrive duplicated by construction.
function dedupeTags(rows) {
  return rows
    .reduce((acc, current) => {
      const existing = acc.find((item) => item.hashtag_id === current.hashtag_id);
      if (existing) {
        existing.times_tagged += 1;
      } else {
        acc.push({ ...current, times_tagged: 1 });
      }
      return acc;
    }, [])
    .sort((a, b) => b.times_tagged - a.times_tagged);
}

// Groups one axis's batched derived_tags read by person and dedupes/ranks each person's rows — the
// batched-read method's own contract is only the fetch and the page-walk, not this shaping.
function groupTagsByPersonKey(rows, idKey, personType) {
  const byPerson = new Map();
  rows.forEach((row) => {
    const key = `${personType}:${row[idKey]}`;
    byPerson.set(key, [...(byPerson.get(key) || []), row]);
  });
  return Object.fromEntries([...byPerson].map(([key, personRows]) => [key, dedupeTags(personRows)]));
}

function rowTags(tags) {
  if (!tags || tags.length === 0) return null;

  return (
    <div className="d-flex flex-wrap gap-1 justify-content-end">
      {tags.slice(0, MAX_HASHTAGS).map((tag) => (
        <Tag
          key={tag.hashtag_id}
          href={expertHashtagUrl(tag.hashtag_name)}
          onClick={() => qahoy.track(HASHTAG_CLICK_EVENT)}
        >
          {tag.hashtag_name}
        </Tag>
      ))}
    </div>
  );
}

function CompanyExperts() {
  const { companyId, company, topLevelIsLoading, viewerContext, setOpenEditModal } = useContext(CompanyContext);
  const editable = !!viewerContext?.editable;
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [experts, setExperts] = useState([]);
  const [highlightedExperts, setHighlightedExperts] = useState([]);
  const [tagsByPersonKey, setTagsByPersonKey] = useState({});
  const [asyncError, setAsyncError] = useState();
  const prFirmView = company?.displayType === "pr_firm";

  const [lastTriggeredAt, scrollTriggerRef] = useInfiniteScroll({ hasMore });

  // Effect to track infinite scrolling intersection observer event, which increments page
  useEffect(() => {
    if (lastTriggeredAt > 0) setPage((prevPage) => prevPage + 1);
  }, [lastTriggeredAt]);

  // Retrieve a company's experts from the API and track the page number
  useEffect(() => {
    let componentIsMounted = true;

    const fetchExperts = async () => {
      try {
        if (companyId) {
          if (page === 1) setIsLoading(true);
          qahoy.track("CompanyExperts#fetchExperts");

          let params = {
            page,
          };

          if (prFirmView) {
            const response = await qapi.representations.getExpertsForPromoterCompany(companyId, params);

            if (componentIsMounted) {
              if (response.data) {
                const results = response.data;
                if (results && results.length > 0) {
                  const expts = results.map((item) => ({
                    featured_company_id: item.promoted_company_id,
                    featured_company_name: item.promoted_company_name,
                    featured_company_slug: item.promoted_company_slug,
                    featured_company_migrate_id: item.promoted_company_migrate_id,
                    featured_title: item.promoted_expert_job_title,
                    id: item.promoted_entity_id,
                    name: item.promoted_entity_name,
                    profile_image_url: item.promoted_entity_profile_image_url,
                    // Every row on this view is a promo-entity expert -- the view names no person
                    // class of its own, so this axis is hardcoded rather than derived.
                    profile_type: "Id::PromoEntity::Expert",
                    // The view's own unique per-row id -- render identity, kept separate from the
                    // person-type composite used for the tag lookup below.
                    row_key: item.row_id,
                    slug: item.promoted_entity_slug,
                    migrate_id: item.promoted_entity_migrate_id,
                  }));
                  setExperts((prevRecords) => appendUniquePeople(prevRecords, expts));
                }
                setHasMore(response.remainingRows > 0);
              } else if (response.error) {
                throw new Error(JSON.stringify(response.error));
              }
              if (componentIsMounted) setIsLoading(false);
            }
          } else {
            params = {
              companyPageVisible: true,
              highlighted: false,
              ...params,
            };

            const response = await qapi.companyJobs.getAllByCompany(companyId, params);

            if (componentIsMounted) {
              if (response.data) {
                const results = response.data;
                if (results && results.length > 0) {
                  const expts = results.map((item) => ({
                    featured_company_id: item.company_id,
                    featured_company_name: item.company_name,
                    featured_company_slug: item.company_slug,
                    featured_company_migrate_id: item.company_migrate_id,
                    featured_title: item.title,
                    id: item.person_id,
                    name: item.person_name,
                    profile_image_url: item.person_profile_image_url,
                    profile_type: item.person_type,
                    // The job's own unique row id -- one person can hold several jobs, so the person
                    // composite alone would collide as a React key; this is render identity, not tag
                    // identity.
                    row_key: item.id,
                    slug: item.person_slug,
                    migrate_id: item.person_migrate_id,
                  }));
                  setExperts((prevRecords) => appendUniquePeople(prevRecords, expts));
                }
                setHasMore(response.remainingRows > 0);
              } else if (response.error) {
                throw new Error(JSON.stringify(response.error));
              }
              if (componentIsMounted) setIsLoading(false);
            }
          }
        }
      } catch (error) {
        setAsyncError(error);
      }
    };

    fetchExperts();

    // Cleanup function to avoid the "Can't perform a React state update on an unmounted component"/memory leak problem
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, page, prFirmView, topLevelIsLoading]);

  useEffect(() => {
    let componentIsMounted = true;

    const fetchHighlightedExperts = async () => {
      try {
        if (companyId) {
          const params = {
            companyPageVisible: true,
            highlighted: true,
          };

          const response = await qapi.companyJobs.getAllByCompany(companyId, params);

          if (componentIsMounted) {
            if (response.data) {
              const results = response.data;
              if (results && results.length > 0) {
                const expts = results.map((item) => ({
                  featured_company_id: item.company_id,
                  featured_company_name: item.company_name,
                  featured_company_slug: item.company_slug,
                  featured_company_migrate_id: item.company_migrate_id,
                  featured_title: item.title,
                  description: item.person_description,
                  id: item.person_id,
                  name: item.person_name,
                  profile_image_url: item.person_profile_image_url,
                  profile_type: item.person_type,
                  // Same job-fan-out shape as the roster above -- render identity, not tag identity.
                  row_key: item.id,
                  slug: item.person_slug,
                  migrate_id: item.person_migrate_id,
                  title: item.title,
                }));
                setHighlightedExperts((prevRecords) => [...prevRecords, ...expts]);
              }
              setHasMore(response.remainingRows > 0);
            } else if (response.error) {
              throw new Error(JSON.stringify(response.error));
            }
            if (componentIsMounted) setIsLoading(false);
          }
        }
      } catch (error) {
        setAsyncError(error);
      }
    };

    fetchHighlightedExperts();

    // Cleanup function to avoid the "Can't perform a React state update on an unmounted component"/memory leak problem
    return () => {
      componentIsMounted = false;
    };
  }, [companyId, topLevelIsLoading]);

  // The roster mixes both axes' person types in one page -- a self-representing promoter's profile
  // id and an expert's promo-entity id can collide numerically -- so this fires one batched read per
  // axis rather than one self-fetch per row. The highlighted rows ride the same two reads: they come
  // from the same underlying view and carry the identical wrong-axis risk through a different card.
  useEffect(() => {
    let componentIsMounted = true;
    const roster = [...experts, ...highlightedExperts];
    if (roster.length === 0) return undefined;

    const promoterIds = [
      ...new Set(roster.filter((person) => person.profile_type === "Id::Profile::Promoter").map((person) => person.id)),
    ];
    const entityIds = [
      ...new Set(roster.filter((person) => person.profile_type !== "Id::Profile::Promoter").map((person) => person.id)),
    ];

    const fetchTags = async () => {
      const [entityResponse, profileResponse] = await Promise.all([
        qapi.hashtags.getAllForPromoEntitiesForDisplay(entityIds, "Id::PromoEntity::Expert"),
        qapi.hashtags.getAllForProfileIdsForDisplay(promoterIds, "Id::Profile::Promoter"),
      ]);

      if (!componentIsMounted) return;

      const entityRows = Array.isArray(entityResponse.data) ? entityResponse.data : [];
      const profileRows = Array.isArray(profileResponse.data) ? profileResponse.data : [];

      setTagsByPersonKey({
        ...groupTagsByPersonKey(entityRows, "promo_entity_id", "Id::PromoEntity::Expert"),
        ...groupTagsByPersonKey(profileRows, "profile_id", "Id::Profile::Promoter"),
      });
    };

    fetchTags();

    return () => {
      componentIsMounted = false;
    };
  }, [experts, highlightedExperts]);

  const renderHighlightedExperts = () => {
    if (!highlightedExperts.length) return null;

    return highlightedExperts.map((expert) => (
      <CompanyHighlightedExpert
        description={expert.description}
        id={expert.id}
        name={expert.name}
        profileImageUrl={expert.profile_image_url}
        profileType={expert.profile_type}
        slug={expert.slug}
        migrateId={expert.migrate_id}
        tags={tagsByPersonKey[`${expert.profile_type}:${expert.id}`]}
        title={expert.title}
        key={`expert-${expert.row_key}`}
      />
    ));
  };

  // Let top-level ErrorBoundary see any async errors
  if (asyncError) throw asyncError;

  // KT July 16 2021 : For the “Experts Form” we hide the edit affordance when prFirmView is true. There is no place to
  // persist the hidden and highlighted booleans in this case. It would take a new concept and significant additional
  // effort to make this work and, we do not anticipate PR agencies as the audience for selling Expert Hub.

  const isEmpty = !isLoading && experts.length === 0 && highlightedExperts.length === 0;
  if (isEmpty && !editable) return null;

  return (
    <SectionCard
      id="company-section-experts"
      title="Our Experts"
      editable={!prFirmView && editable}
      onEdit={() => setOpenEditModal("EXPERTS")}
      editButtonId="modal-button-EXPERTS"
    >
      {isEmpty ? (
        <div className="text-muted">No experts added yet.</div>
      ) : (
        <>
          {highlightedExperts.length ? (
            <>
              <div className="fw-bold small mb-2">Highlighted Experts</div>
              <div className="row mb-4 h-100 w-100">{renderHighlightedExperts()}</div>
            </>
          ) : null}

          <div className="px-0">
            {!isLoading && experts.length > 0 && <div className="fw-bold small mb-2">All Experts</div>}

            <ul className="list-unstyled mb-0">
              {isLoading &&
                Array.from({ length: FIRST_PAGE_PLACEHOLDERS }).map((_, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li className={dividerClasses(index, FIRST_PAGE_PLACEHOLDERS)} key={`expert-placeholder-${index}`}>
                    <CompanyPersonRowSkeleton />
                  </li>
                ))}

              {!isLoading &&
                experts.map((expert, index) => (
                  <li className={dividerClasses(index, experts.length)} key={`expert-${expert.row_key}`}>
                    <ListRow
                      href={expertPath(expert)}
                      imageUrl={expert.profile_image_url}
                      title={expert.name}
                      subtitleTitle={expert.featured_title}
                      subtitleCompanyName={expert.featured_company_name}
                      subtitleCompanyHref={
                        expert.featured_company_slug
                          ? companyUrl({
                              slug: expert.featured_company_slug,
                              migrate_id: expert.featured_company_migrate_id,
                            })
                          : null
                      }
                      tags={rowTags(tagsByPersonKey[`${expert.profile_type}:${expert.id}`])}
                    />
                  </li>
                ))}
            </ul>
            {hasMore && (
              <div ref={scrollTriggerRef}>
                <ul className="list-unstyled mb-0">
                  {Array.from({ length: NEXT_PAGE_PLACEHOLDERS }).map((_, index) => (
                    <li
                      className={dividerClasses(index, NEXT_PAGE_PLACEHOLDERS)}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`expert-next-placeholder-${index}`}
                    >
                      <CompanyPersonRowSkeleton />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </SectionCard>
  );
}

export default CompanyExperts;
